/**
 * Residents directory — redesign v3 "Residents" frame: alphabetised with
 * letter section headers, wing filter chips, search, arch-shaped avatars
 * (brand rule: avatars are arch-shaped, see BRAND-GUIDELINES §1 — the
 * shared `Avatar` in src/ui is a plain circle used across screens outside
 * this restyle's scope, so this arch variant is built locally, same pattern
 * as the CarGlyph/MoonGlyph additions in home.tsx/profile.tsx), call rows
 * where a phone is shared and "Private" otherwise.
 * Wired: GET /api/account/directory (residents).
 * Port of screens-misc.jsx ResidentsScreen.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { str, useGateList } from '@/components/gate/api';
import { InfoNote } from '@/components/gate/bits';
import { CallIconButton, goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { ChipsRow } from '@/features/community/bits';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Chip, EmptyState, Hairline, Input, Screen, Skeleton, TopBar } from '@/ui';

type Resident = { id: string; name: string; flat: string; wing: string; phone?: string };

/** Arch-shaped avatar (36×42, top corners rounded, bottom square) — decorative
 * tint cycles for visual variety only, no data implied. */
function ArchAvatar({ name, index }: { name: string; index: number }) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const tints = [
    { bg: colors.primary500, fg: colors.textOnDark },
    { bg: colors.primary100, fg: colors.primary700 },
    { bg: colors.canvas2, fg: colors.textSecondary },
  ];
  const { bg, fg } = tints[index % tints.length];
  return (
    <View style={{
      width: 36, height: 42, borderRadius: 18, borderBottomLeftRadius: 5, borderBottomRightRadius: 5,
      backgroundColor: bg, alignItems: 'center', justifyContent: 'center',
    }}>
      <AppText weight="700" style={{ fontSize: 12, color: fg }}>{initials}</AppText>
    </View>
  );
}

function toResident(r: unknown, i: number): Resident {
  const flat = str(r, 'flat', 'flatCode') ?? '';
  return {
    id: str(r, 'id') ?? `r${i}`,
    name: str(r, 'name', 'fullName') ?? 'Resident',
    flat,
    wing: (str(r, 'wing', 'tower') ?? flat.charAt(0)).toUpperCase(),
    phone: str(r, 'phone', 'phoneNumber'),
  };
}

export default function ResidentsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state } = useGateList<unknown>('/api/account/directory', 'residents');
  const [q, setQ] = React.useState('');
  const [wing, setWing] = React.useState('ALL');

  const all = items.map(toResident);
  const wings = [...new Set(all.map((r) => r.wing).filter(Boolean))].sort();
  const query = q.trim().toLowerCase();
  const rows = all
    .filter((r) => wing === 'ALL' || r.wing === wing)
    .filter((r) => !query || r.name.toLowerCase().includes(query) || r.flat.toLowerCase().includes(query))
    .sort((a, b) => a.name.localeCompare(b.name));
  const letters = [...new Set(rows.map((r) => r.name.charAt(0).toUpperCase()))];

  return (
    <Screen scroll={false}>
      <TopBar title={t('row_residents')} onBack={goBack} />
      <View style={{ maxWidth: 960, width: '100%', alignSelf: 'center' }}>
      <View style={{ paddingHorizontal: space.s4, paddingTop: space.s2 }}>
        <Input value={q} onChangeText={setQ} placeholder={t('res_search_ph')} />
      </View>
      <ChipsRow>
        <Chip label={t('poll_all_wings')} active={wing === 'ALL'} onPress={() => setWing('ALL')} />
        {wings.map((w) => (
          <Chip key={w} label={t('res_wing', { w })} active={wing === w} onPress={() => setWing(w)} />
        ))}
      </ChipsRow>
      </View>
      <Screen padded>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} h={56} />)}
          </View>
        ) : all.length === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('res_err_title') : t('res_empty_title')}
            body={state === 'error' ? t('res_err_body') : t('res_empty_body')}
          />
        ) : (
          <>
            <InfoNote icon="shield" tint={colors.primary500} style={{ marginBottom: space.s2 }}>
              {t('res_privacy_note')}
            </InfoNote>
            {rows.length === 0 ? (
              <EmptyState
                icon={<Icon name="search" size={40} color={colors.textTertiary} />}
                title={t('res_no_matches')}
                body={t('res_no_matches_body')}
              />
            ) : (
              letters.map((L) => (
                <View key={L}>
                  <AppText variant="label" tone="tertiary" style={{ marginTop: space.s4, marginBottom: space.s1 }}>
                    {L}
                  </AppText>
                  {rows.filter((r) => r.name.charAt(0).toUpperCase() === L).map((r, i) => (
                    <View key={r.id}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s3 }}>
                        <ArchAvatar name={r.name} index={i} />
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <AppText variant="bodyMd" weight="500">{r.name}</AppText>
                          {r.flat ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{r.flat}</AppText> : null}
                        </View>
                        {r.phone
                          ? <CallIconButton phone={r.phone} name={r.name} />
                          : <AppText variant="bodySm" tone="tertiary">{t('res_private')}</AppText>}
                      </View>
                      <Hairline inset={48} />
                    </View>
                  ))}
                </View>
              ))
            )}
          </>
        )}
      </Screen>
    </Screen>
  );
}
